import { createSelector } from "@reduxjs/toolkit"
import {
    selectNonRemovedHotspotImageIdMap,
    selectSortedAndFilteredHotspotsByFlightId,
} from "features/entity-caching/hotspots/selectors"
import { selectInfraredImagesByFlightId } from "features/entity-caching/images/hotspots/selectors"
import { RootState } from "integrations/redux"
import { memoize } from "nodash"
import {
    selectInfraredFlightById,
    selectInfraredFlights,
} from "features/entity-caching/flights/selectors"
import store from "integrations/redux/store"

const root = (state: RootState) => state.hotspotPanel

export const selectHotspotPanelIsOpen = (state: RootState) => root(state).panelOpen
export const selectHotspotPreviewModalIsOpen = (state: RootState) => root(state).previewModalOpen
export const selectHotspotPanelOverlayOptions = (state: RootState) => root(state).overlayOptions
export const selectHotspotPanelListOpen = (state: RootState) => root(state).listOpen

export const selectMergedSiblingFlightIds = memoize((flightId: string) =>
    createSelector(
        [selectInfraredFlightById(flightId), selectInfraredFlights],
        (flight, flights) => {
            if (!flight) return []
            if (!flight.mergeId) return [flight.id]

            return flights.filter((f) => f.mergeId === flight.mergeId).map((f) => f.id)
        }
    )
)

export const selectHotspotPanelImages = memoize((flightId: string) =>
    createSelector(
        [
            selectMergedSiblingFlightIds(flightId),
            selectHotspotPanelFilters,
            selectNonRemovedHotspotImageIdMap(flightId),
        ],
        (flightIds, filters, imageMap) => {
            const state = store.getState()

            const images = flightIds.flatMap((id) =>
                selectInfraredImagesByFlightId(id)(state).sort(
                    (a, b) => (a.takenAt || 0) - (b.takenAt || 0)
                )
            )

            const filtered = images.filter(
                (image) => !filters.withHotspots || imageMap.has(image.id)
            )
            return filtered
        }
    )
)

export const selectHotspotsForFlightId = memoize((flightId: string) =>
    createSelector([selectMergedSiblingFlightIds(flightId)], (flightIds) => {
        const state = store.getState()

        return flightIds.flatMap((id) => selectSortedAndFilteredHotspotsByFlightId(id)(state))
    })
)

export const selectHotspotPanelFilters = (state: RootState) => root(state).filters
export const selectHotspotPanelItems = (state: RootState) => root(state).selected
